import { observable } from '@aha-app/mvc';
import type SelectField from '../fields/SelectField';
import { type RecordId } from '../types';
import type FieldsController from '../controllers/FieldsController';
import { fieldsEnabled } from '../controllers/FieldsController';
import { fieldFromJSON, type SerializedField } from '../fields';
import type BaseField from '../fields/BaseField';
import type ClientBase from './ClientBase';
import DataStore from './DataStore';

type FieldsStore = Record<RecordId, SerializedField>;

const STATUS_FIELD_ID = '7244597799221810616';

const DEFAULT_FIELDS: FieldsStore = {
  [STATUS_FIELD_ID]: {
    name: 'Status',
    type: 'SelectField',
    internal: true,
    tags: true,
    editable: false,
    optionsData: {
      '7244597799221810617': {
        id: '7244597799221810617',
        name: 'Needs review',
        color: '#B2D4F5',
        item_position: 0,
      },
      '7244597799221810618': {
        id: '7244597799221810618',
        name: 'In design',
        color: '#FDC98D',
        item_position: 1,
      },
      '7244597799221810619': {
        id: '7244597799221810619',
        name: 'Ready to develop',
        color: '#FFE48E',
        item_position: 2,
      },
      '7244597799221810620': {
        id: '7244597799221810620',
        name: 'Shipped',
        color: '#7CBB34',
        item_position: 3,
      },
      '7244597799221810621': {
        id: '7244597799221810621',
        name: 'Blocked',
        color: '#EB7A57',
        item_position: 4,
      },
    },
  },
};

// Data store for fields
class DataStoreFields extends DataStore<BaseField> {
  visibleFields: BaseField[];

  constructor(public controller: FieldsController, client: ClientBase) {
    super('fields', client);
  }

  get statusField() {
    return this.findRecordById(STATUS_FIELD_ID) as SelectField;
  }

  updateVisibleFields() {
    this.visibleFields = this.records()
      .filter(field => !field.hidden)
      .sort((a, b) => Number(a.id) - Number(b.id));
  }

  loaded(): void {
    if (!fieldsEnabled()) {
      return;
    }

    // Create default fields, if they do not exist
    (Object.entries(DEFAULT_FIELDS) as Entries<FieldsStore>).forEach(
      ([id, data]) => {
        const { whiteboardController } = this.controller;
        if (!this.findRecordById(id)) {
          const field = fieldFromJSON({
            id,
            controller: whiteboardController,
            ...data,
          });
          this.controller.addField(field);
        }
      }
    );

    this.updateVisibleFields();
  }

  addRecord(record: BaseField) {
    record = observable(record);
    super.addRecord(record);

    this.controller.restoreField(record);
    return record;
  }

  removeRecord(record: BaseField): void {
    record = observable(record);
    super.removeRecord(record);

    this.controller.removeField(record);
  }

  recordDidUpdate(): void {
    this.updateVisibleFields();
  }
}

export default DataStoreFields;
